import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { useNotification } from '../contexts/NotificationContext';
import { PageHeader } from './PageHeader';
import { EMOTIONS, DISTORTIONS, REFRAME_HINTS, getEmotion, type Practice } from '../data/cbtContent';
import './AddJournalEntryPage.css';

type Step = 1 | 2 | 3 | 4;

export const AddJournalEntryPage: React.FC = () => {
  const navigate = useNavigate();
  const { updateBalance } = useUser();
  const { showSuccess } = useNotification();

  const [step, setStep] = useState<Step>(1);

  // Form state
  const [emotion, setEmotion] = useState<string>('');
  const [intensity, setIntensity] = useState<number>(5);
  const [bodyFeelings, setBodyFeelings] = useState<string>('');
  const [situation, setSituation] = useState<string>('');
  const [automaticThought, setAutomaticThought] = useState<string>('');
  const [distortions, setDistortions] = useState<string[]>([]);
  const [alternativeThought, setAlternativeThought] = useState<string>('');
  const [newIntensity, setNewIntensity] = useState<number>(5);
  const [practices, setPractices] = useState<Practice[]>([]);

  const [saving, setSaving] = useState(false);

  const selectedEmotion = getEmotion(emotion);

  // Preload practices on step 4
  useEffect(() => {
    if (step === 4 && emotion && practices.length === 0) {
      apiClient.getPracticesByEmotion(emotion)
        .then((res) => setPractices(res.data ?? []))
        .catch(() => {});
    }
  }, [step, emotion, practices.length]);

  const canProceedFrom1 = emotion && intensity >= 1 && intensity <= 10;
  const canProceedFrom2 = situation.trim().length > 0 && automaticThought.trim().length > 0;

  const handleNext = () => {
    if (step < 4) setStep((s) => (s + 1) as Step);
  };

  const handleBack = () => {
    if (step === 1) {
      navigate('/journal');
      return;
    }
    setStep((s) => (s - 1) as Step);
  };

  const toggleDistortion = (slug: string) => {
    setDistortions((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const handleSubmit = async () => {
    if (!canProceedFrom1 || !canProceedFrom2) return;
    setSaving(true);
    try {
      const response = await apiClient.createJournalEntry({
        emotion,
        intensity,
        body_feelings: bodyFeelings.trim() || null,
        situation: situation.trim(),
        automatic_thought: automaticThought.trim(),
        distortions,
        alternative_thought: alternativeThought.trim() || null,
        new_intensity: alternativeThought.trim() ? newIntensity : null,
      });

      if (response?.balance !== undefined) {
        updateBalance(response.balance);
      }
      showSuccess('Запись сохранена');
      navigate(`/journal/${response.data.id}`);
    } catch (e) {
      console.error('Failed to save journal entry', e);
      alert('Не удалось сохранить запись. Попробуйте ещё раз.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cbt-add-page">
      <PageHeader title="Новая запись" onBack={handleBack} />

      <div className="cbt-progress">
        {[1, 2, 3, 4].map((n) => (
          <div
            key={n}
            className={`cbt-progress-step ${step >= n ? 'cbt-progress-done' : ''} ${step === n ? 'cbt-progress-active' : ''}`}
          />
        ))}
      </div>
      <div className="cbt-step-label">Шаг {step} из 4</div>

      {step === 1 && (
        <section className="cbt-step-section">
          <h2>Какую эмоцию ты сейчас чувствуешь?</h2>
          <div className="cbt-emotions-grid">
            {EMOTIONS.map((e) => (
              <button
                key={e.slug}
                type="button"
                className={`cbt-emotion-chip ${emotion === e.slug ? 'cbt-emotion-selected' : ''}`}
                onClick={() => setEmotion(e.slug)}
                style={emotion === e.slug ? { borderColor: e.color, background: `${e.color}15` } : {}}
              >
                <span className="cbt-emotion-emoji">{e.emoji}</span>
                <span className="cbt-emotion-name">{e.label}</span>
              </button>
            ))}
          </div>

          {emotion && (
            <>
              <h3 className="cbt-sub">Интенсивность: {intensity} / 10</h3>
              <input
                type="range"
                min={1}
                max={10}
                value={intensity}
                onChange={(e) => setIntensity(Number(e.target.value))}
                className="cbt-slider"
                style={{ accentColor: selectedEmotion?.color }}
              />

              <h3 className="cbt-sub">Что ты чувствуешь телом? (необязательно)</h3>
              <textarea
                value={bodyFeelings}
                onChange={(e) => setBodyFeelings(e.target.value)}
                placeholder="Например: ком в горле, тяжесть в груди, напряжение в плечах…"
                className="cbt-textarea"
                rows={3}
                maxLength={2000}
              />
            </>
          )}
        </section>
      )}

      {step === 2 && (
        <section className="cbt-step-section">
          <h2>Что произошло?</h2>
          <textarea
            value={situation}
            onChange={(e) => setSituation(e.target.value)}
            placeholder="Опиши ситуацию которая запустила эту эмоцию…"
            className="cbt-textarea"
            rows={4}
            maxLength={5000}
          />

          <h2>Какая мысль появилась первой?</h2>
          <textarea
            value={automaticThought}
            onChange={(e) => setAutomaticThought(e.target.value)}
            placeholder="Запиши автоматическую мысль — то, что пришло в голову без размышлений…"
            className="cbt-textarea"
            rows={3}
            maxLength={2000}
          />

          <div className="cbt-hint-box">
            <strong>Автоматические мысли</strong> — это первые реакции, которые возникают в голове как рефлекс.
            Они не всегда отражают реальность, и в этом весь смысл — поймать их, разобрать и проверить.
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="cbt-step-section">
          <h2>Какие искажения в твоей мысли?</h2>
          <div className="cbt-thought-quote">«{automaticThought}»</div>
          <p className="cbt-step-hint">Выбери все подходящие. Можно пропустить, если ни одно не подходит.</p>

          <div className="cbt-distortions-list">
            {DISTORTIONS.map((d) => {
              const selected = distortions.includes(d.slug);
              return (
                <button
                  key={d.slug}
                  type="button"
                  className={`cbt-distortion-card ${selected ? 'cbt-distortion-selected' : ''}`}
                  onClick={() => toggleDistortion(d.slug)}
                >
                  <div className="cbt-distortion-head">
                    <span className="cbt-distortion-emoji">{d.emoji}</span>
                    <span className="cbt-distortion-title">{d.label}</span>
                    {selected && <span className="cbt-distortion-check">✓</span>}
                  </div>
                  <div className="cbt-distortion-desc">{d.description}</div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {step === 4 && (
        <section className="cbt-step-section">
          <h2>Сформулируй более взвешенную мысль</h2>

          <div className="cbt-hints-box">
            <strong>Подсказки:</strong>
            <ul>
              {REFRAME_HINTS.map((hint, idx) => (<li key={idx}>{hint}</li>))}
            </ul>
          </div>

          <textarea
            value={alternativeThought}
            onChange={(e) => setAlternativeThought(e.target.value)}
            placeholder="Например: «Это неприятно, но не катастрофа. У меня уже бывало такое и я справился(-лась)…»"
            className="cbt-textarea"
            rows={4}
            maxLength={2000}
          />

          {alternativeThought.trim() && (
            <>
              <h3 className="cbt-sub">Как теперь чувствуешь эмоцию? {newIntensity} / 10</h3>
              <input
                type="range"
                min={1}
                max={10}
                value={newIntensity}
                onChange={(e) => setNewIntensity(Number(e.target.value))}
                className="cbt-slider"
                style={{ accentColor: selectedEmotion?.color }}
              />
              <div className="cbt-intensity-compare">
                <span>Было: <b>{intensity}</b></span>
                <span className="cbt-arrow">→</span>
                <span>Стало: <b>{newIntensity}</b></span>
              </div>
            </>
          )}

          {practices.length > 0 && (
            <div className="cbt-practices-preview">
              <h3 className="cbt-sub">Практики, которые могут помочь</h3>
              {practices.map((p) => (
                <div className="cbt-practice-mini" key={p.slug}>
                  <strong>{p.title}</strong>
                  <span>{p.description}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <div className="cbt-nav">
        {step > 1 && (
          <button type="button" className="cbt-btn cbt-btn-secondary" onClick={handleBack}>
            Назад
          </button>
        )}
        {step < 4 && (
          <button
            type="button"
            className="cbt-btn cbt-btn-primary"
            onClick={handleNext}
            disabled={(step === 1 && !canProceedFrom1) || (step === 2 && !canProceedFrom2)}
          >
            Далее
          </button>
        )}
        {step === 4 && (
          <button
            type="button"
            className="cbt-btn cbt-btn-primary"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Сохраняем…' : 'Сохранить запись'}
          </button>
        )}
      </div>
    </div>
  );
};
